import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "@/components/ScrollToTop";
import SEODebugPanel from "@/components/SEODebugPanel";
import MobileActionBar from "@/components/MobileActionBar";
import Index from "./pages/Index";
import { Loader2 } from "lucide-react";
// Popups disabled

// Lazy load all route components for better performance
const Services = lazy(() => import("./pages/Services"));
const Booking = lazy(() => import("./pages/Booking"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Gallery = lazy(() => import("./pages/Gallery"));
const PetWinnerDetail = lazy(() => import("./pages/PetWinnerDetail"));
const TransformationDetail = lazy(() => import("./pages/TransformationDetail"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FullGroom = lazy(() => import("./pages/services/FullGroom"));
const SpaBath = lazy(() => import("./pages/services/SpaBath"));
const PuppyCut = lazy(() => import("./pages/services/PuppyCut"));
const SeniorCare = lazy(() => import("./pages/services/SeniorCare"));
const CatGrooming = lazy(() => import("./pages/services/CatGrooming"));
const NailCare = lazy(() => import("./pages/services/NailCare"));
const Deshedding = lazy(() => import("./pages/services/Deshedding"));
const AddOns = lazy(() => import("./pages/services/AddOns"));
const CoatConditioning = lazy(() => import("./pages/services/CoatConditioning"));
const CherryCreek = lazy(() => import("./pages/locations/CherryCreek"));
const CapitolHill = lazy(() => import("./pages/locations/CapitolHill"));
const Highlands = lazy(() => import("./pages/locations/Highlands"));
const WashPark = lazy(() => import("./pages/locations/WashPark"));
const RiNo = lazy(() => import("./pages/locations/RiNo"));
const Arvada = lazy(() => import("./pages/locations/Arvada"));
const Aurora = lazy(() => import("./pages/locations/Aurora"));
const Centennial = lazy(() => import("./pages/locations/Centennial"));
const CentralPark = lazy(() => import("./pages/locations/CentralPark"));
const CherryHills = lazy(() => import("./pages/locations/CherryHills"));
const Columbine = lazy(() => import("./pages/locations/Columbine"));
const CommerceCity = lazy(() => import("./pages/locations/CommerceCity"));
const Denver = lazy(() => import("./pages/locations/Denver"));
const DTC = lazy(() => import("./pages/locations/DTC"));
const Edgewater = lazy(() => import("./pages/locations/Edgewater"));
const Englewood = lazy(() => import("./pages/locations/Englewood"));
const Glendale = lazy(() => import("./pages/locations/Glendale"));
const Golden = lazy(() => import("./pages/locations/Golden"));
const GreenwoodVillage = lazy(() => import("./pages/locations/GreenwoodVillage"));
const HighlandsRanch = lazy(() => import("./pages/locations/HighlandsRanch"));
const KenCaryl = lazy(() => import("./pages/locations/KenCaryl"));
const Lakewood = lazy(() => import("./pages/locations/Lakewood"));
const Littleton = lazy(() => import("./pages/locations/Littleton"));
const WheatRidge = lazy(() => import("./pages/locations/WheatRidge"));
const ParkHill = lazy(() => import("./pages/locations/ParkHill"));
const Westminster = lazy(() => import("./pages/locations/Westminster"));
const Thornton = lazy(() => import("./pages/locations/Thornton"));
const LoneTree = lazy(() => import("./pages/locations/LoneTree"));
const DenverMobileGrooming = lazy(() => import("./pages/DenverMobileGrooming"));
const DenverMobileCatGrooming = lazy(() => import("./pages/DenverMobileCatGrooming"));
const SignsYourDogNeedsGrooming = lazy(() => import("./pages/blog/posts/SignsYourDogNeedsGrooming"));
const MobileVsSalon = lazy(() => import("./pages/blog/posts/MobileVsSalon"));
const CherryCreekDogSpots = lazy(() => import("./pages/blog/posts/CherryCreekDogSpots"));
const SeasonalGroomingTips = lazy(() => import("./pages/blog/posts/SeasonalGroomingTips"));
const FirstGroomingPrep = lazy(() => import("./pages/blog/posts/FirstGroomingPrep"));
const WhenCatsNeedGrooming = lazy(() => import("./pages/blog/posts/WhenCatsNeedGrooming"));
const MobileDogGroomingDenver = lazy(() => import("./pages/blog/posts/MobileDogGroomingDenver"));
const SitemapXML = lazy(() => import("./pages/SitemapXML"));
const IndexNowAdmin = lazy(() => import("./pages/admin/IndexNowAdmin"));
const Auth = lazy(() => import("./pages/Auth"));
const BookingsDashboard = lazy(() => import("./pages/admin/BookingsDashboard"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const SEOAudit = lazy(() => import("./pages/admin/SEOAudit"));
const SEOFullAudit = lazy(() => import("./pages/admin/SEOFullAudit"));
const PageEvaluation = lazy(() => import("./pages/admin/PageEvaluation"));
const SEOReportGenerator = lazy(() => import("./pages/admin/SEOReportGenerator"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const Loyalty = lazy(() => import("./pages/Loyalty"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Locations = lazy(() => import("./pages/Locations"));
const DogBreedsHub = lazy(() => import("./pages/breeds/DogBreedsHub"));
const CatBreedsHub = lazy(() => import("./pages/breeds/CatBreedsHub"));
const BreedDetailPage = lazy(() => import("./pages/breeds/BreedDetailPage"));
const PrivatePage = lazy(() => import("./pages/PrivatePage"));
const SupportBoardTest = lazy(() => import("./pages/SupportBoardTest"));

const queryClient = new QueryClient();

const AppContent = () => {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
        <main>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/locations" element={<Locations />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/full-groom" element={<FullGroom />} />
                <Route path="/services/spa-bath" element={<SpaBath />} />
                <Route path="/services/puppy-cut" element={<PuppyCut />} />
                <Route path="/services/senior-care" element={<SeniorCare />} />
                <Route path="/services/cat-grooming" element={<CatGrooming />} />
                <Route path="/services/nail-care" element={<NailCare />} />
                <Route path="/services/deshedding" element={<Deshedding />} />
                <Route path="/services/add-ons" element={<AddOns />} />
                <Route path="/services/coat-conditioning" element={<CoatConditioning />} />
                <Route path="/locations/arvada" element={<Arvada />} />
                <Route path="/locations/aurora" element={<Aurora />} />
                <Route path="/locations/capitol-hill" element={<CapitolHill />} />
                <Route path="/locations/centennial" element={<Centennial />} />
                <Route path="/locations/central-park" element={<CentralPark />} />
                <Route path="/locations/cherry-creek" element={<CherryCreek />} />
                <Route path="/locations/cherry-hills" element={<CherryHills />} />
                <Route path="/locations/columbine" element={<Columbine />} />
                <Route path="/locations/commerce-city" element={<CommerceCity />} />
                <Route path="/locations/denver" element={<Denver />} />
                <Route path="/locations/dtc" element={<DTC />} />
                <Route path="/locations/edgewater" element={<Edgewater />} />
                <Route path="/locations/englewood" element={<Englewood />} />
                <Route path="/locations/glendale" element={<Glendale />} />
                <Route path="/locations/golden" element={<Golden />} />
                <Route path="/locations/greenwood-village" element={<GreenwoodVillage />} />
                <Route path="/locations/highlands" element={<Highlands />} />
                <Route path="/locations/highlands-ranch" element={<HighlandsRanch />} />
                <Route path="/locations/ken-caryl" element={<KenCaryl />} />
                <Route path="/locations/lakewood" element={<Lakewood />} />
                <Route path="/locations/littleton" element={<Littleton />} />
                <Route path="/locations/rino" element={<RiNo />} />
                <Route path="/locations/wash-park" element={<WashPark />} />
                <Route path="/locations/wheat-ridge" element={<WheatRidge />} />
                <Route path="/locations/park-hill" element={<ParkHill />} />
                <Route path="/locations/westminster" element={<Westminster />} />
                <Route path="/locations/thornton" element={<Thornton />} />
                <Route path="/locations/lone-tree" element={<LoneTree />} />
                <Route path="/denver-mobile-dog-grooming" element={<DenverMobileGrooming />} />
                <Route path="/mobile-cat-grooming-denver" element={<DenverMobileCatGrooming />} />
                <Route path="/booking" element={<Booking />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/gallery/winner/:id" element={<PetWinnerDetail />} />
                <Route path="/gallery/transformation/:id" element={<TransformationDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/signs-your-dog-needs-grooming" element={<SignsYourDogNeedsGrooming />} />
                <Route path="/blog/mobile-vs-salon-grooming" element={<MobileVsSalon />} />
                <Route path="/blog/cherry-creek-dog-spots" element={<CherryCreekDogSpots />} />
                <Route path="/blog/seasonal-grooming-tips" element={<SeasonalGroomingTips />} />
                <Route path="/blog/first-grooming-prep" element={<FirstGroomingPrep />} />
                <Route path="/blog/when-cats-need-grooming" element={<WhenCatsNeedGrooming />} />
                <Route path="/blog/mobile-dog-grooming-denver" element={<MobileDogGroomingDenver />} />
                <Route path="/breeds/dogs" element={<DogBreedsHub />} />
                <Route path="/breeds/cats" element={<CatBreedsHub />} />
                <Route path="/breeds/:species/:slug" element={<BreedDetailPage />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/loyalty" element={<Loyalty />} />
                <Route path="/private" element={<PrivatePage />} />
                <Route path="/support-board-test" element={<SupportBoardTest />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/sitemap" element={<Sitemap />} />
                <Route path="/sitemap.xml" element={<SitemapXML />} />
                <Route path="/admin/indexnow" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}><ProtectedRoute><IndexNowAdmin /></ProtectedRoute></Suspense>} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/admin/bookings" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}><ProtectedRoute><BookingsDashboard /></ProtectedRoute></Suspense>} />
                <Route path="/admin/analytics" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}><ProtectedRoute><Analytics /></ProtectedRoute></Suspense>} />
                <Route path="/admin/seo-audit" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}><ProtectedRoute><SEOAudit /></ProtectedRoute></Suspense>} />
                <Route path="/admin/seo-full-audit" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}><SEOFullAudit /></Suspense>} />
                <Route path="/admin/page-evaluation" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}><ProtectedRoute><PageEvaluation /></ProtectedRoute></Suspense>} />
                <Route path="/admin/seo-report" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}><ProtectedRoute><SEOReportGenerator /></ProtectedRoute></Suspense>} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </Suspense>
          <MobileActionBar />
          <SEODebugPanel />
        </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
